import * as THREE from "three";

/**
 * Computes smoothed normals by averaging normals at coincident vertex positions.
 * Stores the result as a "smoothNormal" attribute on the geometry.
 * This produces cleaner outlines on detailed meshes.
 */
export function computeSmoothedNormals(geometry: THREE.BufferGeometry): void {
  const posAttr = geometry.getAttribute("position");
  const normalAttr = geometry.getAttribute("normal");
  if (!posAttr || !normalAttr) return;

  const count = posAttr.count;
  const posMap = new Map<string, THREE.Vector3>();

  for (let i = 0; i < count; i++) {
    const x = posAttr.getX(i);
    const y = posAttr.getY(i);
    const z = posAttr.getZ(i);
    const key = `${x.toFixed(4)},${y.toFixed(4)},${z.toFixed(4)}`;

    const nx = normalAttr.getX(i);
    const ny = normalAttr.getY(i);
    const nz = normalAttr.getZ(i);

    const existing = posMap.get(key);
    if (existing) {
      existing.x += nx;
      existing.y += ny;
      existing.z += nz;
    } else {
      posMap.set(key, new THREE.Vector3(nx, ny, nz));
    }
  }

  for (const normal of posMap.values()) {
    normal.normalize();
  }

  const smoothNormals = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const x = posAttr.getX(i);
    const y = posAttr.getY(i);
    const z = posAttr.getZ(i);
    const key = `${x.toFixed(4)},${y.toFixed(4)},${z.toFixed(4)}`;
    const sn = posMap.get(key)!;
    smoothNormals[i * 3] = sn.x;
    smoothNormals[i * 3 + 1] = sn.y;
    smoothNormals[i * 3 + 2] = sn.z;
  }

  geometry.setAttribute(
    "smoothNormal",
    new THREE.BufferAttribute(smoothNormals, 3)
  );
}
